import React, {FC, useCallback, useEffect, useRef, useState,} from "react";
import {
    ComboBox,
    DefaultButton,
    IComboBox,
    IComboBoxOption,
    Icon,
    Spinner,
    Stack,
    Text,
    VerticalDivider,
} from "@fluentui/react";
import {useTranslation} from "react-i18next";
import Axios, {CancelTokenSource} from "axios";
import "../styles/MainSearchBar.css";
import {useNavigate} from "react-router-dom";
import {useBoolean} from "@fluentui/react-hooks";
import {AppRoute} from "../AppRoutes";
import {theme} from "../index";
import {OmniSearchResourceEnum, OmniSearchResultDto, OmniSearchResultResultDto,} from "../API";
import {OnixCallout} from "./OnixCallout";

export const MainSearchBar = () => {
    const {t} = useTranslation();
    const history = useNavigate();

    const getIcon = useCallback((type: String) => {
        switch (type) {
            case "Users":
                return "ContactList";
            case "CompanySettings":
                return "LinkedDatabase";
            case "Projects":
                return "ProjectCollection";
            case "TicketMessages":
                return "Message";
            case "Tickets":
            case "GeneralOpenTickets":
            case "MyOpenTickets":
                return "Ticket";
            case "Contacts":
                return "ContactCard";
            case "Workers":
                return "RecruitmentManagement";
            case "Companies":
            case "OutsideCompanies":
                return "CityNext";
            case "OpenTasks":
                return "CheckList";
            case "MyOpenTasks":
                return "BulletedList";
            default:
                return "";
        }
    }, []);

    const onPicked = useCallback(
        (picked: OmniSearchResultResultDto) => {
            switch (picked.type) {
                case OmniSearchResourceEnum.Users:
                    history(AppRoute.ADMIN_EDIT_USER(picked.id));
                    break;
                case OmniSearchResourceEnum.CompanySettings:
                    history(AppRoute.EDIT_COMPANY(picked.id as any));
                    break;
                case OmniSearchResourceEnum.Workers:
                    history(AppRoute.EDIT_COMPANY_USER(picked.id));
                    break;
                case OmniSearchResourceEnum.Contacts:
                    history(AppRoute.CONTACT_OVERVIEW(picked.id as any));
                    break;
                case OmniSearchResourceEnum.Projects:
                    history(AppRoute.PROJECT_OVERVIEW(picked.id as any));
                    break;
                case OmniSearchResourceEnum.OpenTasks:
                case OmniSearchResourceEnum.MyOpenTasks:
                case OmniSearchResourceEnum.TicketMessages:
                    history(
                        AppRoute.EDIT_SUPPORT_TICKET(picked.id as any, +picked.detailId),
                    );
                    break;
                case OmniSearchResourceEnum.Tickets:
                case OmniSearchResourceEnum.GeneralOpenTickets:
                case OmniSearchResourceEnum.MyOpenTickets:
                    history(AppRoute.EDIT_SUPPORT_TICKET(picked.id as any));
                    break;
                case OmniSearchResourceEnum.Companies:
                case OmniSearchResourceEnum.OutsideCompanies:
                    history(AppRoute.COMPANY_OVERVIEW(picked.id as any));
                    break;
            }
        },
        [history],
    );

    return (
        <OnixCallout
            preventFocus
            customMaxWidth={700}
            heightless
            title={"search"}
            trigger={(id, show) => (
                <LocalSearchBar
                    id={id}
                    hasResults={(k, results, loadMore) =>
                        show(undefined, undefined, k, {
                            results,
                            loadMore,
                        })
                    }
                />
            )}
        >
            {(
                realign,
                toggleShow,
                renderData: { results: OmniSearchResultDto; loadMore: () => void },
            ) => (
                <Stack horizontal wrap tokens={{childrenGap: 20}}>
                    {Object.keys(renderData?.results ?? []).map((k) => {
                        const result = (renderData.results as any)[
                            k
                            ] as OmniSearchResultResultDto[];
                        if (!result || !result.length) return null;
                        return (
                            <Stack horizontal key={k}>
                                <VerticalDivider/>
                                <Stack
                                    key={k}
                                    tokens={{childrenGap: 10}}
                                    style={{width: 200}}
                                >
                                    <Stack
                                        horizontal
                                        tokens={{childrenGap: 20}}
                                        verticalAlign="center"
                                    >
                                        <div style={{paddingLeft: 10}}>
                                            <Icon
                                                iconName={getIcon(k as any)}
                                                style={{
                                                    fontSize: 24,
                                                    color: theme.palette.themePrimary,
                                                }}
                                            />
                                        </div>
                                        <Text variant={"mediumPlus"}>{t(k)}</Text>
                                    </Stack>
                                    <hr
                                        style={{
                                            width: "100%",
                                            padding: 0,
                                            margin: 0,
                                            color: "rgb(200, 198, 196)",
                                        }}
                                    />
                                    <Stack tokens={{childrenGap: 0}}>
                                        {result.map((r: OmniSearchResultResultDto) => (
                                            <Stack
                                                key={r.id + r.detailId}
                                                tokens={{childrenGap: 0, padding: 10}}
                                                onClick={() => {
                                                    onPicked(r);
                                                    toggleShow();
                                                }}
                                                className="has-clickable-action"
                                            >
                                                <Text>{r.title}</Text>
                                                <Text
                                                    variant={"xSmall"}
                                                    style={{color: theme.palette?.themePrimary}}
                                                >
                                                    {r.description}
                                                </Text>
                                            </Stack>
                                        ))}
                                        {result.length && result.length >= 3 && (
                                            <DefaultButton onClick={renderData.loadMore}>
                                                Učitaj više
                                            </DefaultButton>
                                        )}
                                    </Stack>
                                </Stack>
                            </Stack>
                        );
                    })}
                </Stack>
            )}
        </OnixCallout>
    );
};

interface ILocalSearchBar {
    hasResults: (
        b: boolean,
        results?: OmniSearchResultDto,
        loadMore?: () => void,
    ) => void;
    id: string;
}

const LocalSearchBar: FC<ILocalSearchBar> = ({hasResults, id}) => {
    const {t} = useTranslation();
    const [value, setValue] = useState<string>();
    const [results, setResults] = useState<OmniSearchResultDto>();
    const comboBoxRef = useRef<IComboBox>(null);
    const history = useNavigate();
    const cancelToken = useRef<CancelTokenSource>(Axios.CancelToken.source());
    const [loading, {setTrue: setLoading, setFalse: setNotLoading}] =
        useBoolean(false);
    const [resultCount, setResultCount] = useState<number>(5);

    const loadMore = useCallback(() => {
        setResultCount(resultCount + 5);
    }, [resultCount]);

    const checkIfHasResults = useCallback(() => {
        if (results) {
            if (Object.keys(results).length > 0) {
                hasResults(true, results, loadMore);
            } else {
                hasResults(false, undefined, loadMore);
            }
        } else {
            hasResults(false, undefined, loadMore);
        }
    }, [results, loadMore]);

    useEffect(() => {
        checkIfHasResults();
    }, [results]);

    useEffect(() => {
        if (cancelToken?.current) {
            cancelToken.current.cancel("Clearing");
        }

        cancelToken.current = Axios.CancelToken.source();

        const timer = window.setTimeout(() => {
            if (value) {
                setLoading();
                Axios.get<OmniSearchResultDto>("/OmniSearch", {
                    params: {
                        query: value,
                        limit: resultCount,
                    },
                    cancelToken: cancelToken.current.token,
                })
                    .then(({data}) => {
                        console.log("DATA", data);
                        setResults(data);
                        if (data && Object.keys(data).length) {
                            comboBoxRef.current?.focus(true);
                        }
                    })
                    .finally(setNotLoading);
            }
        }, 300);

        return () => window.clearTimeout(timer);
    }, [value, cancelToken, resultCount]);

    const inputChanged = useCallback(
        ({target: {value}}: any) => {
            setImmediate(() => setValue(value));
        },
        [setValue, value],
    );

    const change = useCallback(
        (
            event: React.FormEvent<IComboBox>,
            option?: IComboBoxOption,
            index?: number,
            value?: string,
        ) => {
            if (option) {
                if (option.data) {
                    const picked = option.data as OmniSearchResultResultDto;

                    switch (picked.type) {
                        case OmniSearchResourceEnum.Users:
                            history(AppRoute.ADMIN_EDIT_USER(picked.id));
                            break;
                        case OmniSearchResourceEnum.CompanySettings:
                            history(AppRoute.EDIT_COMPANY(picked.id as any));
                            break;
                        case OmniSearchResourceEnum.Workers:
                            history(AppRoute.EDIT_COMPANY_USER(picked.id));
                            break;
                        case OmniSearchResourceEnum.Contacts:
                            history(AppRoute.CONTACT_OVERVIEW(picked.id as any));
                            break;
                        case OmniSearchResourceEnum.Projects:
                            history(AppRoute.PROJECT_OVERVIEW(picked.id as any));
                            break;
                        case OmniSearchResourceEnum.OpenTasks:
                        case OmniSearchResourceEnum.MyOpenTasks:
                        case OmniSearchResourceEnum.TicketMessages:
                            history(
                                AppRoute.EDIT_SUPPORT_TICKET(
                                    picked.id as any,
                                    +picked.detailId,
                                ),
                            );
                            break;
                        case OmniSearchResourceEnum.Tickets:
                        case OmniSearchResourceEnum.GeneralOpenTickets:
                        case OmniSearchResourceEnum.MyOpenTickets:
                            history(AppRoute.EDIT_SUPPORT_TICKET(picked.id as any));
                            break;
                        case OmniSearchResourceEnum.Companies:
                        case OmniSearchResourceEnum.OutsideCompanies:
                            history(AppRoute.COMPANY_OVERVIEW(picked.id as any));
                            break;
                    }

                    setValue("");
                }
            }
        },
        [],
    );

    return (
        <div id="search-box-main">
            <ComboBox
                text={value}
                id={id}
                inputMode={"text"}
                componentRef={comboBoxRef}
                onInput={inputChanged}
                onDismiss={() => {
                }}
                onMenuDismiss={() => {
                }}
                onMenuDismissed={() => {
                }}
                calloutProps={{
                    onDismiss: () => {
                    },
                    preventDismissOnScroll: true,
                }}
                options={[]}
                // options={results ? Object.keys(results).map(value1 => {
                //     const data: IComboBoxOption[] = [];

                //     data.push({
                //         itemType: SelectableOptionMenuItemType.Header,
                //         key: value1,
                //         text: t(value1) ?? " - - "
                //     });

                //     (results as any)[value1 as any].forEach((value2: OmniSearchResultResultDto) => {
                //         data.push({
                //             key: value1 + value2.id + value2.detailId,
                //             text: t(value2.title) ?? " - - ",
                //             data: value2
                //         });
                //     });

                //     if (data.length > 1) return data;
                //     return [];
                // }).flatMap(m => m).map(m => (m)) : []}
                multiSelect={false}
                // onRenderOption={props => props?.itemType == SelectableOptionMenuItemType.Header ? <Text
                //     // variant={"xSmall"}
                //     styles={{
                //         root: {
                //             fontWeight: "bold"
                //         }
                //     }}>{t(props?.text)}</Text> : <Stack tokens={{ childrenGap: 0 }}>
                //     <Text>
                //         {props?.text}
                //     </Text>
                //     <Text variant={"xSmall"} style={{ color: theme.palette?.themePrimary }}>
                //         {props?.data.description}
                //     </Text>
                // </Stack>}
                onChange={change}
                onFocus={() => {
                    comboBoxRef.current?.focus(true);
                    // checkIfHasResults();
                }}
                useComboBoxAsMenuWidth
                placeholder={t("search")}
                allowFreeform
                autoComplete={"off"}
                autoCapitalize={"none"}
                autoCorrect={"none"}
                spellCheck={false}
                selectedKey={null}
                unselectable="on"
                iconButtonProps={{
                    iconProps: {
                        iconName: "search",
                    },
                    onClick: () => {
                        comboBoxRef.current?.focus(true);
                        checkIfHasResults();
                    },
                    onRenderIcon: loading ? () => <Spinner/> : undefined,
                }}
            />
        </div>
    );
};
